/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/set-state-in-effect */
/**
 * AuthConfigContext - Provides auth configuration from backend
 *
 * This context fetches the authentication configuration from /auth/config
 * at startup and makes it available throughout the application.
 *
 * The backend returns different configurations based on AUTH_METHOD env var:
 * - password: Show email/password login form
 * - azure_ad: Show "Sign in with Microsoft" button
 * - okta: Show "Sign in with Okta" button
 * - google: Show "Sign in with Google" button
 *
 * Usage:
 *   import { useAuthConfig } from '../contexts/AuthConfigContext';
 *
 *   const authConfig = useAuthConfig();
 *   if (authConfig.auth_method === 'azure_ad') {
 *     // Render Azure AD login button
 *   }
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from 'react'
import { logger } from '../utils/logger'
import { config as appConfig } from '../config'
import { createServiceFetch } from '../utils/validateFetchUrl'
import { isServiceUnavailableError } from '../utils/serviceAvailability'

const authConfigFetch = createServiceFetch(appConfig.api.baseUrl)

/**
 * Authentication configuration from backend
 */
export interface AuthConfig {
  /** Active auth method: password, azure_ad, okta, google */
  auth_method: 'password' | 'azure_ad' | 'okta' | 'google' | 'saml'
  /** Display name for the provider */
  provider_name: string
  /** Configuration error if auth method is misconfigured (e.g., missing env vars) */
  config_error?: string
  /** For password auth: login endpoint */
  login_endpoint: string | null
  /** For OAuth: initial auth URL (optional, can be fetched) */
  auth_url: string | null
  /** For OIDC: public client ID */
  client_id: string | null
  /** For Azure AD: tenant ID */
  tenant_id: string | null
  /** For Okta: issuer URL */
  issuer_url: string | null
  /** OAuth redirect URI */
  redirect_uri: string | null
  /** Required OAuth scopes */
  scopes: string[]
  /** Provider-specific features */
  features: Record<string, unknown>
  /**
   * Minutes of UI inactivity before auto-logout (0 = disabled).
   * From backend SESSION_INACTIVITY_TIMEOUT_MINUTES.
   */
  session_inactivity_timeout_minutes?: number
}

interface AuthConfigContextType {
  /** Current auth configuration */
  config: AuthConfig | null
  /** Whether config is being loaded */
  isLoading: boolean
  /** Error if config fetch failed */
  error: Error | null
  /**
   * True when /auth/config could not be reached (502/503/504, network failure).
   * The app must show the "service unavailable" screen instead of a login form,
   * because the real auth method (SSO vs password) is unknown.
   */
  isServiceUnavailable: boolean
  /** Refresh config from backend */
  refresh: () => Promise<void>
}

const AuthConfigContext = createContext<AuthConfigContextType | undefined>(undefined)

// Default config for fallback
const DEFAULT_CONFIG: AuthConfig = {
  auth_method: 'password',
  provider_name: 'Password',
  login_endpoint: '/auth/login',
  auth_url: null,
  client_id: null,
  tenant_id: null,
  issuer_url: null,
  redirect_uri: null,
  scopes: [],
  features: {},
}

interface AuthConfigProviderProps {
  children: ReactNode
}

export const AuthConfigProvider: React.FC<AuthConfigProviderProps> = ({ children }) => {
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false)

  const fetchConfig = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const baseUrl = appConfig.api.baseUrl.replace(/\/+$/, '')
      const url = `${baseUrl}/auth/config`
      let tenantId: string | null = null
      try {
        tenantId = localStorage.getItem('tagp-tenant-id')
      } catch {
        // ignore
      }
      const headers: Record<string, string> = {}
      if (tenantId) headers['X-Tenant-ID'] = tenantId
      const response = await authConfigFetch(url, { headers })

      if (!response.ok) {
        const httpError = Object.assign(
          new Error(`Failed to fetch auth config: ${response.status}`),
          { status: response.status }
        )
        throw httpError
      }

      const data: AuthConfig = await response.json()
      setAuthConfig(data)
      setIsServiceUnavailable(false)
      logger.info(`Auth config loaded: method=${data.auth_method}`)
    } catch (err) {
      const unreachable = isServiceUnavailableError(err)
      if (unreachable) {
        // Backend/gateway is down (typically a rolling deploy). We don't know the
        // tenant's auth method, so falling back to the password form would show a
        // login screen that can't work — surface the outage instead.
        logger.warn('Auth config unavailable: backend unreachable', err)
      } else {
        logger.error('Failed to load auth config', err)
      }
      setError(err instanceof Error ? err : new Error('Unknown error'))
      setIsServiceUnavailable(unreachable)
      setAuthConfig(unreachable ? null : DEFAULT_CONFIG)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchConfig()
  }, [])

  const contextValue = useMemo<AuthConfigContextType>(
    () => ({
      config: authConfig,
      isLoading,
      error,
      isServiceUnavailable,
      refresh: fetchConfig,
    }),
    [authConfig, isLoading, error, isServiceUnavailable]
  )

  return <AuthConfigContext.Provider value={contextValue}>{children}</AuthConfigContext.Provider>
}

/**
 * Hook to access auth configuration
 *
 * @throws Error if used outside AuthConfigProvider
 */
export const useAuthConfig = (): AuthConfigContextType => {
  const context = useContext(AuthConfigContext)
  if (context === undefined) {
    throw new Error('useAuthConfig must be used within AuthConfigProvider')
  }
  return context
}

/**
 * Hook to get just the auth method
 */
export const useAuthMethod = (): AuthConfig['auth_method'] | null => {
  const { config } = useAuthConfig()
  return config?.auth_method ?? null
}

/**
 * Check if current auth method is password-based
 */
export const useIsPasswordAuth = (): boolean => {
  const { config } = useAuthConfig()
  return config?.auth_method === 'password'
}

/**
 * Check if current auth method uses OIDC (Azure AD, Okta, Google)
 */
export const useIsOidcAuth = (): boolean => {
  const { config } = useAuthConfig()
  return (
    config?.auth_method === 'azure_ad' ||
    config?.auth_method === 'okta' ||
    config?.auth_method === 'google'
  )
}

/**
 * Check if auth configuration has an error (e.g., missing env vars)
 */
export const useHasConfigError = (): boolean => {
  const { config } = useAuthConfig()
  return !!config?.config_error
}

/**
 * Get the config error message if present
 */
export const useConfigError = (): string | undefined => {
  const { config } = useAuthConfig()
  return config?.config_error
}

export default AuthConfigContext
